import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { UnlockIcon } from './Icons';

/**
 * FeatureUpgradePrompt — shown to an under-tier org when a tier-gated feature's flag is ON but the
 * org's subscription doesn't meet the requirement (2026-06-08 rollout). It replaces the old
 * "visible feature → backend 403 → generic error" path with a clear upgrade affordance.
 *
 * Driven by `tierGate` from useFeatures(): a feature page renders this instead of its content when
 * `tierGate[<featureKey>]` is set (the value is the required tier). The backend computes tierGate
 * with the SAME meetsSubscriptionRequirement() the route gate uses, so this shows exactly when a
 * 403 would have occurred.
 */
const TIER_LABEL: Record<string, string> = {
  core: 'Core',
  pro: 'Pro',
  leader: 'Leader',
};

export interface FeatureUpgradePromptProps {
  /** Human name, e.g. "Evolution Archive". */
  featureName: string;
  /** Required subscription tier key, e.g. 'pro' | 'core'. */
  requiredTier: string;
  /** One line, Helm's voice — what the org unlocks by upgrading. */
  benefit: string;
  /** Override the CTA action. Defaults to navigating to /pricing. */
  onUpgrade?: () => void;
  /** Override the glyph. */
  icon?: React.ReactNode;
  /**
   * Override the dismiss action. Defaults to `navigate(-1)` — correct for a full-page gate
   * (Evolution Archive, Template Library). An action-triggered overlay (a sheet/modal/popover
   * rendered on top of a screen the user is actively on, e.g. ShareSheet's sharing paywall —
   * Spec 249) should pass its own close handler instead, so dismiss doesn't navigate the user
   * away from the screen underneath it.
   */
  onDismiss?: () => void;
  /** Label for the dismiss action. Defaults to "Maybe later". */
  dismissLabel?: string;
  /**
   * Whether to show the primary "Upgrade to {Tier}" button. Defaults to true. Set false for a
   * role that can't act on billing (e.g. Submissive/Member in a hierarchy — Spec 249's
   * create-side sharing paywall) — a button implying purchase authority the viewer doesn't
   * have is worse than no button. When false, renders a plain-text link (same /pricing
   * destination as the button, via onUpgrade if provided) instead of a button.
   */
  canUpgrade?: boolean;
  /** Label for the plain-text link shown when `canUpgrade` is false. Defaults to "See what's
   *  included in {Tier}". */
  upgradeLinkLabel?: string;
}

export const FeatureUpgradePrompt: React.FC<FeatureUpgradePromptProps> = ({
  featureName,
  requiredTier,
  benefit,
  onUpgrade,
  icon,
  onDismiss,
  dismissLabel = 'Maybe later',
  canUpgrade = true,
  upgradeLinkLabel,
}) => {
  const navigate = useNavigate();
  const tierLabel = TIER_LABEL[requiredTier] ?? requiredTier;
  const handleUpgrade = (): void => {
    if (onUpgrade) onUpgrade();
    else navigate('/pricing');
  };
  const handleDismiss = (): void => {
    if (onDismiss) onDismiss();
    else navigate(-1);
  };

  return (
    <Card variant="bordered" padding="lg" data-testid="feature-upgrade-prompt">
      <div className="text-center max-w-sm mx-auto py-2">
        <div className="w-14 h-14 rounded-full bg-surface border border-border-light flex items-center justify-center mx-auto mb-4 text-2xl" aria-hidden="true">
          {icon ?? <UnlockIcon size={24} />}
        </div>
        <h3 className="text-lg font-semibold text-text-primary m-0 mb-2">{featureName}</h3>
        <p className="text-xs text-text-tertiary m-0 mb-4">
          Available on <span className="font-semibold text-accent">{tierLabel}</span>
        </p>
        <p className="text-sm text-text-secondary m-0 mb-6">{benefit}</p>
        {canUpgrade ? (
          <Button
            variant="primary"
            onClick={handleUpgrade}
            className="w-full mb-2"
            data-testid="feature-upgrade-cta"
          >
            Upgrade to {tierLabel}
          </Button>
        ) : (
          <Button
            variant="text"
            type="button"
            onClick={handleUpgrade}
            className="w-full mb-2 underline underline-offset-2"
            data-testid="feature-upgrade-link"
          >
            {upgradeLinkLabel ?? `See what's included in ${tierLabel}`}
          </Button>
        )}
        <Button
          variant="text"
          type="button"
          onClick={handleDismiss}
          className="p-1"
          data-testid="feature-upgrade-dismiss"
        >
          {dismissLabel}
        </Button>
      </div>
    </Card>
  );
};
